import { Navigate, useLocation, type ReactNode } from "react-router-dom";
import AppLoadingScreen from "@/components/AppLoadingScreen";
import { getEffectiveDashboardRole } from "@/lib/dashboardNav";
import { getAuthState } from "@/lib/authSession";

const getSession = () => {
  const auth = getAuthState();
  return {
    loggedIn: auth.loggedIn,
    role: auth.role,
    user: auth.user,
  };
};

export const RouteFallback = ({ title = "Loading page..." }: { title?: string }) => (
  <AppLoadingScreen variant="shell" />
);

export const ProtectedRoute = ({ children }: { children: ReactNode }) => {
  const session = getSession();
  return session.loggedIn ? <>{children}</> : <Navigate to="/login" replace />;
};

export const PublicAuthRoute = ({ children }: { children: ReactNode }) => {
  const session = getSession();
  if (!session.loggedIn) {
    return <>{children}</>;
  }

  if (session.role === "admin") return <Navigate to="/admin" replace />;
  if (session.role === "accountant") return <Navigate to="/accountant" replace />;
  return <Navigate to="/logistics" replace />;
};

export const PublicAuthRedirectRoute = ({ to }: { to: string }) => (
  <PublicAuthRoute>
    <Navigate to={to} replace />
  </PublicAuthRoute>
);

export const AdminRoute = ({ children }: { children: ReactNode }) => {
  const session = getSession();
  return session.loggedIn && ["admin", "accountant"].includes(session.role) ? <>{children}</> : <Navigate to="/login" replace />;
};

export const ProviderRoute = ({ children }: { children: ReactNode }) => {
  const session = getSession();
  if (!session.loggedIn) {
    return <Navigate to="/login" replace />;
  }

  return session.role === "provider" ? <>{children}</> : <Navigate to="/dashboard" replace />;
};

export const DashboardHomeRedirect = () => {
  const session = getSession();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const previewRole = searchParams.get("previewRole");
  const previewUser = searchParams.get("previewUser");
  const effectiveRole = getEffectiveDashboardRole(session.role as never, previewRole);

  if (effectiveRole === "agent") {
    const params = new URLSearchParams();
    if (previewRole) params.set("previewRole", effectiveRole);
    if (previewUser) params.set("previewUser", previewUser);
    const suffix = params.toString() ? `?${params.toString()}` : "";
    return <Navigate to={`/dashboard/agent${suffix}`} replace />;
  }

  return <Navigate to="/dashboard/overview" replace />;
};
